import { useEffect, useMemo, useState } from 'react'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'

import { SLIDE_CAP } from '../constants'
import * as presentationService from '../services/presentationService'
import type { AssetOption, Presentation, PortfolioLocations, PortfolioOrder, PresentationTemplate, Story } from '../types/presentation.types'
import { AssetPicker } from './AssetPicker'

/**
 * A portfolio review in one step (MYD-507): the portfolio's overview story,
 * then a story for each of its locations, each from a template.
 *
 * Two homes:
 *  - `create`, from the Portfolio page: makes a new deck and opens it.
 *  - `insert`, from Add story in the editor: picks a portfolio, and hands the
 *    stories to the editor to insert as one undoable edit.
 *
 * Only locations the user can read are listed; the server applies the same
 * rule again, and leaves out of each story what the user may not see there.
 * The slide count is shown before anything is made, because a deck holds at
 * most SLIDE_CAP slides and a big portfolio can pass it.
 */

type Mode =
	| { kind: 'create'; portfolio: { entityId: string; name: string } }
	| { kind: 'insert'; existingSlides: number }

interface PortfolioDeckDialogProps {
	open: boolean
	mode: Mode
	onClose: () => void
	/** create: the new deck. */
	onCreated?: (presentation: Presentation, dropped: number) => void
	/** insert: the stories to put in the deck. */
	onInsert?: (stories: Story[], dropped: number) => void
}

const NONE = ''
const ORDER_LABELS: Record<PortfolioOrder, string> = { portfolio: 'The portfolio’s order', name: 'Name', sqft: 'Largest first' }

const orderBy = (locations: PortfolioLocations['locations'], order: PortfolioOrder) => {
	if (order === 'name') return [...locations].sort((a, b) => a.name.localeCompare(b.name))
	if (order === 'sqft') return [...locations].sort((a, b) => b.sqft - a.sqft || a.name.localeCompare(b.name))
	return locations
}

export function PortfolioDeckDialog({ open, mode, onClose, onCreated, onInsert }: PortfolioDeckDialogProps) {
	const [picked, setPicked] = useState<AssetOption | null>(null)
	const [templates, setTemplates] = useState<PresentationTemplate[]>([])
	const [overviewKey, setOverviewKey] = useState('portfolio.overview')
	const [locationKey, setLocationKey] = useState('location.brief')
	const [order, setOrder] = useState<PortfolioOrder>('portfolio')
	const [data, setData] = useState<PortfolioLocations | null>(null)
	const [chosen, setChosen] = useState<Set<string>>(new Set())
	const [name, setName] = useState('')
	const [loading, setLoading] = useState(false)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const portfolioId = mode.kind === 'create' ? mode.portfolio.entityId : picked?.entityId ?? null

	useEffect(() => {
		if (!open) return
		setPicked(null)
		setOrder('portfolio')
		setError(null)
		setName(mode.kind === 'create' ? `${mode.portfolio.name} review` : '')
		presentationService
			.listTemplates()
			.then(setTemplates)
			.catch(() => setTemplates([]))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	// The portfolio's readable locations; every one chosen to start with.
	useEffect(() => {
		if (!open || !portfolioId) {
			setData(null)
			return
		}
		let cancelled = false
		setLoading(true)
		presentationService
			.getPortfolioLocations(portfolioId)
			.then(found => {
				if (cancelled) return
				setData(found)
				setChosen(new Set(found.locations.map(location => location.entityId)))
			})
			.catch(() => !cancelled && setError('This portfolio could not be loaded.'))
			.finally(() => !cancelled && setLoading(false))
		return () => {
			cancelled = true
		}
	}, [open, portfolioId])

	const portfolioTemplates = templates.filter(template => template.scope === 'portfolio')
	const locationTemplates = templates.filter(template => template.scope === 'location')
	const overview = portfolioTemplates.find(template => template.key === overviewKey) ?? null
	const perLocation = locationTemplates.find(template => template.key === locationKey) ?? null
	const ordered = useMemo(() => orderBy(data?.locations ?? [], order), [data, order])

	// An upper bound: slides the user may not see are left out on the way.
	const existing = mode.kind === 'insert' ? mode.existingSlides : 0
	const slides = existing + (overview?.slideCount ?? 0) + chosen.size * (perLocation?.slideCount ?? 0)
	const overCap = slides > SLIDE_CAP

	const toggle = (entityId: string) =>
		setChosen(current => {
			const next = new Set(current)
			if (next.has(entityId)) next.delete(entityId)
			else next.add(entityId)
			return next
		})

	const submit = async () => {
		if (!portfolioId || !perLocation || chosen.size === 0) return
		setBusy(true)
		setError(null)
		const request = {
			portfolioId,
			overviewTemplateKey: overviewKey === NONE ? null : overviewKey,
			locationTemplateKey: locationKey,
			// In the order shown, which is the order the stories will have.
			locationIds: ordered.filter(location => chosen.has(location.entityId)).map(location => location.entityId),
			order,
		}
		try {
			if (mode.kind === 'create') {
				const created = await presentationService.createPresentationFromPortfolio({ ...request, name: name.trim() || undefined })
				onCreated?.(created, created.dropped ?? 0)
			} else {
				const made = await presentationService.getPortfolioStories({ ...request, existingSlides: mode.existingSlides })
				onInsert?.(made.stories, made.dropped)
			}
		} catch (cause: any) {
			setError(cause?.response?.data?.message ?? 'The stories could not be made. Try again.')
		} finally {
			setBusy(false)
		}
	}

	const title = mode.kind === 'create' ? 'Create a portfolio presentation' : 'Add a portfolio and its locations'

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[620px]">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>An overview of the portfolio, then a story for each location, in the order you choose.</DialogDescription>
				</DialogHeader>

				<div className="max-h-[62vh] space-y-4 overflow-y-auto px-5 py-4">
					{mode.kind === 'insert' && (
						<div className="space-y-2">
							<Label>Portfolio</Label>
							<AssetPicker value={picked} onChange={setPicked} scopes={['portfolio']} />
						</div>
					)}

					{mode.kind === 'create' && (
						<div className="space-y-1.5">
							<Label htmlFor="portfolio-deck-name">Presentation name</Label>
							<Input id="portfolio-deck-name" value={name} maxLength={140} onChange={event => setName(event.target.value)} />
						</div>
					)}

					<div className="grid gap-3 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="portfolio-deck-overview">Overview</Label>
							<Select id="portfolio-deck-overview" value={overviewKey} onChange={event => setOverviewKey(event.target.value)}>
								<option value={NONE}>No overview</option>
								{portfolioTemplates.map(template => (
									<option key={template.key} value={template.key}>
										{template.name} ({template.slideCount})
									</option>
								))}
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="portfolio-deck-location">Each location</Label>
							<Select id="portfolio-deck-location" value={locationKey} onChange={event => setLocationKey(event.target.value)}>
								{locationTemplates.map(template => (
									<option key={template.key} value={template.key}>
										{template.name} ({template.slideCount})
									</option>
								))}
							</Select>
						</div>
					</div>

					{portfolioId && (
						<div className="space-y-2">
							<div className="flex items-end justify-between gap-3">
								<div className="space-y-1.5">
									<Label htmlFor="portfolio-deck-order">Order</Label>
									<Select id="portfolio-deck-order" value={order} onChange={event => setOrder(event.target.value as PortfolioOrder)}>
										{(Object.keys(ORDER_LABELS) as PortfolioOrder[]).map(value => (
											<option key={value} value={value}>
												{ORDER_LABELS[value]}
											</option>
										))}
									</Select>
								</div>
								{data && data.locations.length > 0 && (
									<div className="flex gap-3 pb-2 text-xs">
										<button type="button" className="text-primary hover:underline" onClick={() => setChosen(new Set(data.locations.map(location => location.entityId)))}>
											All
										</button>
										<button type="button" className="text-primary hover:underline" onClick={() => setChosen(new Set())}>
											None
										</button>
									</div>
								)}
							</div>

							{loading ? (
								<p className="text-xs text-muted-foreground">Loading locations...</p>
							) : data && data.locations.length === 0 ? (
								<p className="text-sm text-gray-500">This portfolio has no locations you can open.</p>
							) : (
								<ul className="max-h-60 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200" aria-label="Locations">
									{ordered.map(location => (
										<li key={location.entityId}>
											<label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-gray-50">
												<input type="checkbox" className="h-4 w-4 accent-primary" checked={chosen.has(location.entityId)} onChange={() => toggle(location.entityId)} />
												<span className="min-w-0 flex-1">
													<span className="block truncate text-sm text-gray-900">{location.name}</span>
													<span className="block truncate text-xs text-gray-500">{location.place}</span>
												</span>
												{location.sqft > 0 && <span className="shrink-0 text-xs tabular-nums text-gray-500">{location.sqft.toLocaleString()} sq ft</span>}
											</label>
										</li>
									))}
								</ul>
							)}

							{data && data.locations.length > 0 && (
								<p className={overCap ? 'text-xs font-medium text-destructive' : 'text-xs text-muted-foreground'} role={overCap ? 'alert' : undefined}>
									{chosen.size} {chosen.size === 1 ? 'location' : 'locations'} · up to {slides} slides
									{mode.kind === 'insert' && existing > 0 && ` including the ${existing} already in this deck`}
									{overCap && `. A presentation holds at most ${SLIDE_CAP}: choose fewer locations or a shorter template.`}
								</p>
							)}
						</div>
					)}
					{error && <p className="text-sm text-destructive">{error}</p>}
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose} disabled={busy}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={busy || !portfolioId || !perLocation || chosen.size === 0 || overCap}>
						{busy ? 'Making...' : mode.kind === 'create' ? 'Create presentation' : 'Add stories'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default PortfolioDeckDialog
